import { Map } from "immutable"

import PresentationReducerBuilder from "./build-presentation-reducer"
import { serialiseSearchCriteria } from "../paging/paging-reducer-utils"
import {
    COMMUNITY_ITEM_REQUEST as ITEM_REQUEST,
    COMMUNITY_ITEM_RESPONSE as ITEM_RESPONSE,
    COMMUNITY_ITEM_CACHED_RESPONSE as ITEM_CACHED_RESPONSE,
    COMMUNITY_ITEM_ERROR_RESPONSE as ITEM_ERROR_RESPONSE,
    COMMUNITY_ITEM_SAVE_RESPONSE as ITEM_SAVE_RESPONSE,
    COMMUNITY_LIST_SEARCH_REQUEST as LIST_SEARCH_REQUEST,
    COMMUNITY_LIST_REQUEST as LIST_PAGE_REQUEST,
    COMMUNITY_LIST_RESPONSE as LIST_RESPONSE,
    COMMUNITY_LIST_CACHED_RESPONSE as LIST_CACHED_RESPONSE,
    COMMUNITY_PARTICIPANT_LIST_REQUEST,
    COMMUNITY_PARTICIPANT_LIST_SEARCH_REQUEST,
    COMMUNITY_MEMBERS_ADD_LIST_SEARCH_REQUEST,
    COMMUNITY_MEMBERS_LIST_REQUEST,
    COMMUNITY_MANAGERS_LIST_REQUEST,
    COMMUNITY_FOLLOWERS_LIST_REQUEST,
    COMMUNITY_INVITED_LIST_REQUEST,
    COMMUNITY_AWAITING_APPROVAL_LIST_REQUEST,
    COMMUNITY_SET_FILTER as SET_FILTER,
    COMMUNITY_UPDATE_FILTER_CRITERIA as UPDATE_FILTER_CRITERIA,
    COMMUNITY_CLEAR_FILTER_CRITERIA as CLEAR_FILTER_CRITERIA,
    COMMUNITY_UPDATE_IMAGE as UPDATE_IMAGE,
    COMMUNITY_UPDATE_MODEL as UPDATE_MODEL,
    COMMUNITY_APPLY_MODEL_UPDATE as APPLY_MODEL_UPDATE,
    COMMUNITY_CANCEL_MODEL_UPDATE as CANCEL_MODEL_UPDATE,
    COMMUNITY_ADD_NEW as ADD_NEW,
    COMMUNITY_SET_EDIT_MODE as SET_EDIT_MODE,
    COMMUNITY_CANCEL_ADDING_MEMBERS,
    COMMUNITY_APPLY_ADD_SELECTED_MEMBERS,
    COMMUNITY_SELECT_ADD_MEMBER,
    COMMUNITY_SELECT_REMOVE_PARTICIPANT,
    COMMUNITY_PROMOTE_MEMBER
} from "../../actions/action-types"
import {
    toUI as communityToUI,
    updateCommunityChangeModel,
    updateCommunityCreateModel,
    CommunityModel,
    CommunityCreateModel
} from "../../models/entities/community"
import {
    CommunityFilterModel,
    updateCommunitySearchFilterModel
} from "../../models/filters/community-filter"
import { defaultSearchCriteria } from "../paging/community-paging-reducer"

const reducerBuilder = new PresentationReducerBuilder()
const defaultReducer = reducerBuilder
    .actionsToHandle({
        ADD_NEW,
        UPDATE_IMAGE,
        UPDATE_MODEL,
        APPLY_MODEL_UPDATE,
        CANCEL_MODEL_UPDATE,
        CLEAR_FILTER_CRITERIA,
        UPDATE_FILTER_CRITERIA,
        SET_FILTER,
        SET_EDIT_MODE,
        ITEM_REQUEST,
        ITEM_RESPONSE,
        ITEM_SAVE_RESPONSE,
        ITEM_CACHED_RESPONSE,
        ITEM_ERROR_RESPONSE,
        LIST_PAGE_REQUEST,
        LIST_SEARCH_REQUEST,
        LIST_RESPONSE,
        LIST_CACHED_RESPONSE
    })
    .updateChangeModel(updateCommunityChangeModel)
    .updateCreateModel(updateCommunityCreateModel)
    .workingModelToUI(communityToUI)
    .workingModelClass(CommunityModel)
    .createModelClass(CommunityCreateModel)
    .workingFilterModelClass(CommunityFilterModel)
    .updateSearchFilterModel(updateCommunitySearchFilterModel)
    .canCancel(() => true)
    .defaultState({
        workingModel: new CommunityModel(),
        searchCriteria: serialiseSearchCriteria(defaultSearchCriteria),
        participantSearchTerm: "",
        membersSearchCriteria: "name=",
        membersAddSearchCriteria: "name=",
        managersSearchCriteria: "name=",
        followersSearchCriteria: "name=",
        invitedSearchCriteria: "name=",
        awaitingApprovalSearchCriteria: "name=",
        workingFilterModel: new CommunityFilterModel(),
        searchFilterModel: defaultSearchCriteria,
        isAddingMembers: false,
        membersSelectedToAdd: [],
        membersSelectedToRemove: [],
        membersToAdd: [],
        managersToAdd: [],
        membersToRemove: [],
        managersToRemove: [],
        followersToRemove: [],
        selectedParticipantType: "members"
    })
    .build()

export default (state = undefined, action) => {
    const defaultReduction = defaultReducer(state, action)
    const { type, participant, participantType } = action
    const { isCreateMode } = defaultReduction
    let { updateModel } = defaultReduction
    switch (type) {
        case ADD_NEW: {
            return {
                ...defaultReduction,
                participantSearchTerm: "",
                membersSearchCriteria: "name=",
                membersAddSearchCriteria: "name=",
                managersSearchCriteria: "name=",
                followersSearchCriteria: "name=",
                invitedSearchCriteria: "name=",
                awaitingApprovalSearchCriteria: "name=",
                isAddingMembers: false,
                membersSelectedToAdd: [],
                membersSelectedToRemove: [],
                membersToAdd: [],
                managersToAdd: [],
                membersToRemove: [],
                managersToRemove: [],
                followersToRemove: []
            }
        }
        case SET_EDIT_MODE:
        case CANCEL_MODEL_UPDATE:
            return {
                ...defaultReduction,
                isAddingMembers: false,
                membersSelectedToAdd: [],
                membersSelectedToRemove: [],
                membersToAdd: [],
                managersToAdd: [],
                membersToRemove: [],
                managersToRemove: [],
                followersToRemove: []
            }
        case UPDATE_MODEL:
            // If sub_type is not set and parent exists, then set default of "sub_community"
            let { workingModel } = defaultReduction
            const parent = workingModel.get("parent")
            const subType = workingModel.get("sub_type")
            if (parent && subType === "") {
                workingModel = workingModel.set("sub_type", "sub_community")
                updateModel = updateModel.set("sub_type", "sub_community")
            } else if (!parent) {
                workingModel = workingModel.set("sub_type", "")
                updateModel = updateModel.delete("sub_type")
            }
            return {
                ...defaultReduction,
                workingModel,
                updateModel
            }
        case ITEM_RESPONSE:
        case ITEM_CACHED_RESPONSE:
            return {
                ...defaultReduction,
                isAddingMembers: false,
                membersSelectedToAdd: [],
                membersSelectedToRemove: [],
                membersToAdd: [],
                managersToAdd: [],
                membersToRemove: [],
                managersToRemove: [],
                followersToRemove: []
            }
        case COMMUNITY_PARTICIPANT_LIST_REQUEST:
            return {
                ...defaultReduction,
                selectedParticipantType: action.participantType
            }
        case COMMUNITY_PARTICIPANT_LIST_SEARCH_REQUEST:
            return {
                ...defaultReduction,
                participantSearchTerm: action.searchCriteria.name,
                [`${
                    defaultReduction.selectedParticipantType
                }SearchCriteria`]: serialiseSearchCriteria(
                    action.searchCriteria
                )
            }
        case COMMUNITY_MEMBERS_ADD_LIST_SEARCH_REQUEST:
            return {
                ...defaultReduction,
                participantSearchTerm: action.searchCriteria.name,
                membersAddSearchCriteria: serialiseSearchCriteria(
                    action.searchCriteria
                ),
                isAddingMembers: true
            }
        case COMMUNITY_MEMBERS_LIST_REQUEST:
            return {
                ...defaultReduction,
                membersSearchCriteria: serialiseSearchCriteria(
                    action.searchCriteria
                )
            }
        case COMMUNITY_MANAGERS_LIST_REQUEST:
            return {
                ...defaultReduction,
                managersSearchCriteria: serialiseSearchCriteria(
                    action.searchCriteria
                )
            }
        case COMMUNITY_FOLLOWERS_LIST_REQUEST:
            return {
                ...defaultReduction,
                followersSearchCriteria: serialiseSearchCriteria(
                    action.searchCriteria
                )
            }
        case COMMUNITY_INVITED_LIST_REQUEST:
            return {
                ...defaultReduction,
                invitedSearchCriteria: serialiseSearchCriteria(
                    action.searchCriteria
                )
            }
        case COMMUNITY_AWAITING_APPROVAL_LIST_REQUEST:
            return {
                ...defaultReduction,
                awaitingApprovalSearchCriteria: serialiseSearchCriteria(
                    action.searchCriteria
                )
            }
        case COMMUNITY_CANCEL_ADDING_MEMBERS:
            return {
                ...defaultReduction,
                isAddingMembers: false,
                membersSelectedToAdd: [],
                membersSelectedToRemove: []
            }
        case COMMUNITY_SELECT_ADD_MEMBER: {
            let membersSelectedToAdd = defaultReduction.membersSelectedToAdd
            let membersSelectedToRemove =
                defaultReduction.membersSelectedToRemove
            const { isAdded, id: selectedId } = action.member
            const isAlreadyAdded = membersSelectedToAdd
                .concat(defaultReduction.membersToAdd)
                .some(({ id }) => selectedId === id)
            if (isAdded && !isAlreadyAdded)
                membersSelectedToAdd = membersSelectedToAdd.concat([
                    action.member
                ])
            else if (!isAdded) {
                if (membersSelectedToAdd.some(({ id }) => selectedId === id))
                    membersSelectedToAdd = membersSelectedToAdd.filter(
                        ({ id }) => id !== selectedId
                    )
                else
                    membersSelectedToRemove = membersSelectedToRemove.concat([
                        selectedId
                    ])
            }
            return {
                ...defaultReduction,
                membersSelectedToAdd,
                membersSelectedToRemove
            }
        }
        case COMMUNITY_PROMOTE_MEMBER: {
            const { member } = action
            const membersAddAccessor = isCreateMode ? "members" : "members_add"
            const managersAddAccessor = isCreateMode
                ? "managers"
                : "managers_add"
            let { managersToAdd, managersToRemove } = defaultReduction
            const previousMembers_add = updateModel.get(membersAddAccessor, [])
            const wasBeingAdded = previousMembers_add.indexOf(member.id) !== -1
            let newMembersToAdd = defaultReduction.membersToAdd
            if (wasBeingAdded) {
                updateModel = updateModel.set(
                    membersAddAccessor,
                    previousMembers_add.filter(id => id !== member.id)
                )
                newMembersToAdd = defaultReduction.membersToAdd.map(
                    newMember => {
                        if (newMember.id === member.id) {
                            return {
                                ...newMember,
                                [`isPromoted_${
                                    defaultReduction.activeId
                                }`]: true
                            }
                        }
                        return newMember
                    }
                )
            }

            if (
                !isCreateMode &&
                updateModel
                    .get("managers_remove", [])
                    .some(id => id === member.id)
            ) {
                updateModel = updateModel.set(
                    "managers_remove",
                    updateModel
                        .get("managers_remove")
                        .filter(id => id !== member.id)
                )
                managersToRemove = managersToRemove.filter(
                    ({ id }) => id !== member.id
                )
            } else {
                updateModel = updateModel.set(
                    managersAddAccessor,
                    updateModel.get(managersAddAccessor, []).concat([member.id])
                )
                managersToAdd = managersToAdd.concat([member])
            }
            return {
                ...defaultReduction,
                updateModel,
                membersToAdd: newMembersToAdd,
                managersToAdd,
                managersToRemove,
                canSave: true,
                canCancel: true
            }
        }
        case COMMUNITY_SELECT_REMOVE_PARTICIPANT: {
            const participant_addAccessor = isCreateMode
                ? participantType
                : `${participantType}_add`
            const participant_removeAccessor = `${participantType}_remove`
            const previousParticipant_add = updateModel.get(
                participant_addAccessor,
                []
            )
            const wasBeingAdded = previousParticipant_add.some(
                id => id === participant.id
            )
            let newParticipantsToAdd =
                defaultReduction[`${participantType}ToAdd`]
            let newMembersToAdd = defaultReduction.membersToAdd
            if (wasBeingAdded) {
                updateModel = updateModel.set(
                    participant_addAccessor,
                    previousParticipant_add.filter(id => id !== participant.id)
                )
                if (participantType === "managers") {
                    newMembersToAdd = defaultReduction.membersToAdd.filter(
                        ({ id }) => id !== participant.id
                    )
                }
                if (newParticipantsToAdd) {
                    newParticipantsToAdd = newParticipantsToAdd.filter(
                        ({ id }) => id !== participant.id
                    )
                }
            } else if (!isCreateMode) {
                updateModel = updateModel.set(
                    participant_removeAccessor,
                    updateModel
                        .get(participant_removeAccessor, [])
                        .concat([participant.id])
                )
            }
            const newState = {
                ...defaultReduction,
                [`${participantType}ToRemove`]: updateModel.get(
                    participant_removeAccessor,
                    []
                ),
                updateModel,
                membersToAdd: newMembersToAdd,
                canSave: true
            }
            if (newParticipantsToAdd)
                newState[`${participantType}ToAdd`] = newParticipantsToAdd
            return newState
        }
        case COMMUNITY_APPLY_ADD_SELECTED_MEMBERS: {
            const membersToAdd = defaultReduction.membersToAdd.filter(
                ({ id }) =>
                    defaultReduction.membersSelectedToRemove.indexOf(id) === -1
            )
            const newMembersToAdd = membersToAdd
                .concat(defaultReduction.membersSelectedToAdd)
                .map(member => ({
                    ...member,
                    is_member: {
                        [defaultReduction.activeId]: true
                    },
                    is_manager: {
                        [defaultReduction.activeId]: false
                    }
                }))
            const updateModel = isCreateMode
                ? updateCommunityCreateModel
                : updateCommunityChangeModel
            return {
                ...defaultReduction,
                isAddingMembers: false,
                membersToAdd: newMembersToAdd,
                updateModel: updateModel(
                    "members",
                    newMembersToAdd,
                    defaultReduction.updateModel,
                    Map({ members: membersToAdd })
                ),
                canSave: true,
                canCancel: true,
                membersSelectedToAdd: [],
                membersSelectedToRemove: [],
                participantSearchTerm: "",
                membersSearchCriteria:
                    "name=" +
                    (isCreateMode
                        ? ""
                        : `&communityId=${defaultReduction.activeId}`)
            }
        }
        default:
            return defaultReduction
    }
}
